import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, Loader2, Image as ImageIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";

const STATUSES = ["new", "contacted", "quoted", "scheduled", "completed", "lost"];
const COLORS = {
  new: "bg-primary/10 text-primary", contacted: "bg-blue-100 text-blue-700", quoted: "bg-amber-100 text-amber-700",
  scheduled: "bg-purple-100 text-purple-700", completed: "bg-green-100 text-green-700", lost: "bg-muted text-muted-foreground",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const token = localStorage.getItem("cg_token");

  const load = () => {
    setLoading(true);
    api.get(`/leads${filter !== "all" ? `?status=${filter}` : ""}`).then((r) => setLeads(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Marked as ${status}`);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      if (active?.id === id) setActive({ ...active, status });
    } catch { toast.error("Update failed"); }
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/leads/${active.id}`, { admin_notes: notes });
      toast.success("Notes saved");
      setActive({ ...active, admin_notes: notes });
    } catch { toast.error("Save failed"); }
  };

  const openLead = (l) => { setActive(l); setNotes(l.admin_notes || ""); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Leads</h1>
          <p className="text-muted-foreground">Manage incoming quote requests.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="lead-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : leads.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center text-muted-foreground">No leads found.</div>
      ) : (
        <div className="bg-card border border-border overflow-hidden">
          <div className="divide-y divide-border">
            {leads.map((l) => (
              <div key={l.id} className="p-5 hover:bg-muted/40 transition-colors" data-testid={`lead-row-${l.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <button onClick={() => openLead(l)} className="flex-1 text-left min-w-0" data-testid={`lead-open-${l.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{l.name}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 ${COLORS[l.status]}`}>{l.status.toUpperCase()}</span>
                      {l.photo_ids?.length > 0 && <span className="text-xs text-muted-foreground flex items-center gap-1"><ImageIcon className="h-3 w-3" /> {l.photo_ids.length}</span>}
                    </div>
                    <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</span>
                      <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {l.email}</span>
                      <span className="font-medium text-foreground">{l.service_type}</span>
                      {l.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.city}</span>}
                    </div>
                  </button>
                  <div className="lg:w-44 shrink-0">
                    <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                      <SelectTrigger className="rounded-sm h-9" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl">{active.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> <a href={`tel:${active.phone}`} className="hover:text-primary">{active.phone}</a></div>
                  <div className="flex items-center gap-2 truncate"><Mail className="h-4 w-4 text-primary" /> <a href={`mailto:${active.email}`} className="hover:text-primary truncate">{active.email}</a></div>
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {[active.address, active.city].filter(Boolean).join(", ") || "—"}</div>
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {active.timeline || "—"}</div>
                </div>
                <div className="bg-muted p-4">
                  <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Service</div>
                  <div className="font-semibold">{active.service_type}</div>
                  <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mt-3">Details</div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{active.project_details}</p>
                </div>
                {active.inspiration_projects?.length > 0 && (
                  <div>
                    <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Inspiration From Gallery</div>
                    <div className="flex flex-wrap gap-2">
                      {active.inspiration_projects.map((t, i) => (
                        <span key={i} className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1" data-testid={`lead-inspiration-${i}`}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {active.photo_ids?.length > 0 && (
                  <div>
                    <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Photos</div>
                    <div className="grid grid-cols-3 gap-2">
                      {active.photo_ids.map((id) => (
                        <a key={id} href={`${fileUrl(id)}?auth=${token}`} target="_blank" rel="noreferrer">
                          <img src={`${fileUrl(id)}?auth=${token}`} alt="lead upload" className="w-full aspect-square object-cover border border-border" data-testid={`lead-photo-${id}`} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Internal Notes</div>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-sm" data-testid="lead-notes" />
                  <Button onClick={saveNotes} size="sm" className="mt-2 bg-secondary rounded-sm font-bold" data-testid="save-notes">Save Notes</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
